import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { withRouter, RouteComponentProps } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";

import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import ContentStudioMovementsStore from "stores/ContentStudioMovementsStore";

import Page from "components/Layout/Page";
import ConfirmationDialog from "components/ConfirmationDialog";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import Placeholder from "components/Placeholder";
import RestoreArchivedButton from "components/RestoreArchivedButton";
import SortingControl from "components/SortingControl";

import NavBar from "../NavBar";
import MovementCard from "./MovementCard";
import MovementForm from "./MovementForm";

import { SNACKBAR_TYPES } from "components/Snackbar";

import { TableSortType } from "stores/templates/TableStore";

import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  MovementsCardWrapper,
  ScrollLoader,
} from "./styles";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";

@observer
class Movements extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;

  @computed get movements() {
    return ContentStudioMovementsStore.items;
  }

  @computed get movementListIsEmpty() {
    return this.movements.length === 0;
  }

  componentDidMount() {
    ContentStudioMovementsStore.fetch();
  }

  componentWillUnmount() {
    ContentStudioMovementsStore.reset();
  }

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  fetchMoreMovements = async () => {
    const { setMessage } = this.props;

    try {
      await ContentStudioMovementsStore.fetchMoreMovements();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    ContentStudioMovementsStore.applySearch(value);
    ContentStudioMovementsStore.fetch();
  };

  onSearchClear = () => {
    ContentStudioMovementsStore.clearSearch();
    ContentStudioMovementsStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    ContentStudioMovementsStore.applyOrder(value);
    ContentStudioMovementsStore.resetPage();
    ContentStudioMovementsStore.fetch();
  };

  onManageMovement = (initialValues?: any) => {
    const { openPopup } = this.props;

    openPopup({
      key: "ManageMovement",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <MovementForm
          isLoading={ContentStudioMovementsStore.isMovementManaging}
          isFormLoading={ContentStudioMovementsStore.isMovementLoading}
          initialValues={initialValues}
          onSubmit={this.onManageMovementSubmit(close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
        />
      ),
    });
  };

  onManageMovementSubmit = (close: () => void) => async (values: any) => {
    const { setMessage } = this.props;
    const { id, file, title, description, videoUrl } = values;

    const data = {
      title: title?.trim(),
      description: description?.trim(),
      videoUrl: videoUrl?.trim(),
    };

    try {
      if (!id) {
        await ContentStudioMovementsStore.createMovement({ file, ...data });
      } else {
        const payload = { id, ...data };
        await ContentStudioMovementsStore.editMovementInfo(payload);

        if (typeof file !== "string") {
          const payload = { file, id };
          await ContentStudioMovementsStore.editMovementImage(payload);
        }
      }

      this.resetIsPopupDirty();
      close();
      ContentStudioMovementsStore.resetPage();
      ContentStudioMovementsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onAddMovement = () => {
    this.onManageMovement();
  };

  onEditMovement = (movement: any) => () => {
    const id = movement.id;

    this.onManageMovement({ id });
  };

  onArchive = (movement: any) => () => {
    const { openPopup } = this.props;

    const movementName = movement.title;
    const movementId = movement.id;

    openPopup({
      key: "ArchiveMovement",
      canCloseByOuterClick: true,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title={"Archive Movement"}
          text={`Are you sure you want to archive ${movementName} movement? It will no longer be displayed to user in the app and moved to the Archive in the Admin panel.`}
          acceptText={"Archive"}
          declineText={"Cancel"}
          isLoading={ContentStudioMovementsStore.isMovementManaging}
          onDecline={close}
          onAccept={this.archiveMovement(movementId, close)}
        />
      ),
    });
  };

  archiveMovement = (id: number, close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      await ContentStudioMovementsStore.archiveMovement(id);
      close();
      ContentStudioMovementsStore.resetPage();
      ContentStudioMovementsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  render() {
    return (
      <Page
        title="Movements"
        topControls={<NavBar />}
        controls={
          <>
            <PageControl>
              <RestoreArchivedButton />
            </PageControl>
            <PageControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddMovement}
              >
                Add Movement
              </Button>
            </PageControl>
          </>
        }
        isLoading={ContentStudioMovementsStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={ContentStudioMovementsStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={
                  ContentStudioMovementsStore.orderContext.appliedOrderInfo
                }
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {!this.movementListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.movements.length}
            hasMore={ContentStudioMovementsStore.hasMoreInfo}
            next={this.fetchMoreMovements}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.movements.map(movement => (
              <MovementsCardWrapper key={movement.id}>
                <MovementCard
                  title={movement.title}
                  duration={movement.durationText}
                  image={movement.imageUrl}
                  onArchive={this.onArchive(movement)}
                  onEdit={this.onEditMovement(movement)}
                />
              </MovementsCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              ContentStudioMovementsStore.search
                ? "No matches found. Please try another search."
                : "No movements added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Movements)));
